import { Plugin, PluginKey } from '@tiptap/pm/state';
import { type EditorView } from '@tiptap/pm/view';
import { Extension } from '@tiptap/react';

// Backspace, Delete and the arrows (plus their macOS Ctrl-b/d/f/h/n/p twins)
// fall through to prosemirror-view's `captureKeyDown`, whose
// `skipIgnoredNodes` walks `parentNode` up from the DOM selection's focus until
// it meets `view.dom`. A focus outside the view walks off the document and
// throws on `null.pmViewDesc`.
const WALK_KEY_CODES = new Set([8, 37, 38, 39, 40, 46]);
const CTRL_WALK_KEY_CODES = new Set([66, 68, 70, 72, 78, 80]);

const isSelectionWalkKey = (event: KeyboardEvent): boolean =>
  WALK_KEY_CODES.has(event.keyCode) ||
  (event.ctrlKey && CTRL_WALK_KEY_CODES.has(event.keyCode));

const isDomSelectionInView = (view: EditorView): boolean => {
  const focusNode = view.dom.ownerDocument.getSelection()?.focusNode;
  return !focusNode || view.dom.contains(focusNode);
};

export const KeydownSelectionGuard = Extension.create({
  name: 'keydown-selection-guard',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('keydown-selection-guard'),
        props: {
          handleKeyDown(view, event) {
            if (!isSelectionWalkKey(event) || isDomSelectionInView(view)) {
              return false;
            }
            // Re-sync the DOM selection to the editor's own so the key acts
            // where the caret is drawn; swallow it if that is not possible
            // (read-only or detached view).
            view.focus();
            return !isDomSelectionInView(view);
          },
        },
      }),
    ];
  },
});
